import React from "react";

const QUICK_ACTIONS: Array<{
  label: string;
  desc: string;
  prompt: string;
  icon: React.ReactElement;
}> = [
  {
    label: "Ask about your email",
    desc: "Summaries, drafts, follow-ups",
    prompt: "What's in my inbox that needs attention today?",
    icon: (
      <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      >
        <rect x="1.5" y="3" width="13" height="10" rx="1.5" />
        <path d="M1.5 3L8 9l6.5-6" />
      </svg>
    ),
  },
  {
    label: "Create a task",
    desc: "Add something to your plate",
    prompt: "Create a task: ",
    icon: (
      <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      >
        <rect x="2" y="3" width="12" height="11" rx="2" />
        <path d="M5 7.5l2 2 4-4" />
      </svg>
    ),
  },
  {
    label: "Show my schedule",
    desc: "Meetings and events coming up",
    prompt: "What's on my schedule for today and tomorrow?",
    icon: (
      <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      >
        <rect x="2" y="3" width="12" height="11" rx="1.5" />
        <path d="M2 6.5h12M5.5 2v2.5M10.5 2v2.5" />
      </svg>
    ),
  },
  {
    label: "Help me draft something",
    desc: "An email, doc, or note",
    prompt: "Help me draft ",
    icon: (
      <svg
        width="16"
        height="16"
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      >
        <path d="M11.5 1.5l3 3-9 9H2.5v-3l9-9z" />
        <path d="M9.5 3.5l3 3" />
      </svg>
    ),
  },
];

interface EmptyStateProps {
  agentName?: string;
  onAction: (text: string) => void;
  onPrefill?: (text: string) => void;
}

function getAgentInitial(name: string | undefined): string {
  const trimmed = (name ?? "").trim();
  if (!trimmed) return "A";
  // Handle Unicode names — grab the first code-point character.
  const first = Array.from(trimmed)[0] ?? "A";
  return first.toUpperCase();
}

export default function EmptyState({ agentName, onAction, onPrefill }: EmptyStateProps) {
  const initial = getAgentInitial(agentName);
  const displayName = (agentName ?? "").trim() || "your agent";
  return (
    <div className="flex items-center justify-center h-full">
      <div className="text-center max-w-lg w-full px-4">
        <div className="flex justify-center mb-4">
          <div
            className="w-14 h-14 rounded-full flex items-center justify-center shadow-sm"
            style={{
              background: "linear-gradient(135deg, var(--c-primary), var(--c-info))",
              color: "white",
            }}
            aria-label={`${displayName} avatar`}
          >
            <span
              className="text-lg font-semibold select-none"
              style={{ fontFamily: "inherit", letterSpacing: "0.01em" }}
            >
              {initial}
            </span>
          </div>
        </div>
        <h2 className="text-lg font-semibold mb-1" style={{ color: "var(--c-text)" }}>
          How can I help you today?
        </h2>
        <p className="text-sm mb-6" style={{ color: "var(--c-text-faint)" }}>
          Ask me anything, or try one of these to get started.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-md mx-auto text-left">
          {QUICK_ACTIONS.map((card) => (
            <button
              key={card.label}
              onClick={() => {
                const text = card.prompt;
                const endsWithSpace = text.endsWith(" ") || text.endsWith(": ");
                // Prompts that trail with a space/colon expect the user to finish typing —
                // prefill the input. Full-sentence prompts can fire immediately.
                if (endsWithSpace && onPrefill) {
                  onPrefill(text);
                } else {
                  onAction(text);
                }
              }}
              className="flex items-start gap-2.5 px-3 py-2.5 rounded-lg text-xs transition-all"
              style={{
                border: "1px solid var(--c-border)",
                backgroundColor: "var(--c-surface)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = "var(--c-primary)";
                e.currentTarget.style.backgroundColor = "var(--c-primary-subtle)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "var(--c-border)";
                e.currentTarget.style.backgroundColor = "var(--c-surface)";
              }}
            >
              <span className="mt-0.5 shrink-0" style={{ color: "var(--c-text-secondary)" }}>
                {card.icon}
              </span>
              <div className="min-w-0">
                <p className="font-medium" style={{ color: "var(--c-text)" }}>
                  {card.label}
                </p>
                <p style={{ color: "var(--c-text-faint)" }}>{card.desc}</p>
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
